import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ChevronLeft, ChevronRight, Building2, Clock } from "lucide-react";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { isOperationalRole } from "@/lib/roles";

function pad2(n) { return String(n).padStart(2, "0"); }
function todayAR() { return new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }); }
function monthLabel(y, m) { return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("es-AR", { month: "long", year: "numeric", timeZone: "UTC" }); }

export default function Schedule() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = role === "admin" || role === "supervisor";
  const isMobile = isOperationalRole(role);
  const backLink = role === "admin" ? "/admin" : role === "supervisor" ? "/supervisor" : "/vigilador";

  return (
    <div style={{ padding: isMobile ? "8px 16px" : "20px 32px", maxWidth: 1100, margin: "0 auto" }} data-testid="schedule-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Cronograma de servicio</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>
        {canManage ? "Programación de turnos normales de servicio por objetivo. Distinto de Guardias Pasivas (retén)." : "Tus turnos programados de servicio."}
      </p>
      {canManage ? <ScheduleCalendar /> : <MySchedule />}
    </div>
  );
}

function MySchedule() {
  const today = todayAR();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [month, setMonth] = useState(Number(today.slice(5, 7)));
  const [data, setData] = useState(null);
  const monthStr = `${year}-${pad2(month)}`;

  const load = async () => {
    try { const { data: d } = await api.get("/schedule/mine", { params: { month: monthStr } }); setData(d); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div data-testid="my-schedule">
      <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 14, marginBottom: 14 }}>
        <button onClick={() => changeMonth(-1)} style={{ border: "1px solid var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: 6, cursor: "pointer" }} data-testid="my-schedule-prev"><ChevronLeft size={16}/></button>
        <div style={{ fontWeight: 900, fontSize: 14, textTransform: "capitalize" }} data-testid="my-schedule-month">{monthLabel(year, month)}</div>
        <button onClick={() => changeMonth(1)} style={{ border: "1px solid var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: 6, cursor: "pointer" }} data-testid="my-schedule-next"><ChevronRight size={16}/></button>
      </div>

      {data == null && <div className="skel" style={{ height: 100 }} />}
      {data && (
        <>
          <div className="card" style={{ padding: 14, marginBottom: 14, background: "#0A0A0A", color: "#FFFBF4", display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="my-schedule-total">
            <span style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Total del mes</span>
            <span style={{ fontSize: 20, fontWeight: 900 }}>{data.total_hours} h</span>
          </div>
          {(() => {
            // Fix BP (2026-11-03): también los días de guardia pasiva (se cargan aparte, en Guardias pasivas).
            const passive = (data.passive_guards || []).map((g) => ({ ...g, _passive: true }));
            const all = [...data.assignments, ...passive].sort((x, y) => (x.date + (x.time_from || "")).localeCompare(y.date + (y.time_from || "")));
            if (all.length === 0) {
              return <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }} data-testid="my-schedule-empty">Sin turnos programados este mes.</div>;
            }
            return (
            <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="my-schedule-list">
              {all.map((a) => a._passive ? (
                <div key={a.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", borderLeft: "4px solid #7c3aed" }} data-testid={`my-schedule-passive-${a.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Guardia pasiva</div>
                    <span className="badge purple">GUARDIA PASIVA</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}><Clock size={11}/>{a.date}{a.time_from ? ` · ${a.time_from}–${a.time_to || ""}` : " · todo el día"}</div>
                  {a.note && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", marginTop: 2 }}>{a.note}</div>}
                </div>
              ) : (
                <div key={a.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`my-schedule-row-${a.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A", display: "flex", alignItems: "center", gap: 5 }}><Building2 size={12}/>{a.client_name}{a.site_name ? ` · ${a.site_name}` : ""}</div>
                    {a.is_day_off
                      // Fix BT (2026-11-04): mañana turquesa, tarde violeta (mismos colores que el Cronograma).
                      ? <span className="badge gray" style={a.day_off_half ? { background: a.day_off_half === "tarde" ? "#9333ea" : "#0891b2", color: "#fff" } : undefined}>{a.day_off_half === "manana" ? "MEDIO FRANCO · MAÑANA" : a.day_off_half === "tarde" ? "MEDIO FRANCO · TARDE" : "FRANCO"}</span>
                      : <span style={{ fontSize: 12, fontWeight: 800 }}>{a.hours ?? "—"} h</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}><Clock size={11}/>{a.date}{!a.is_day_off ? ` · ${a.time_from}–${a.time_to}` : ""}</div>
                  {a.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", marginTop: 2 }}>{a.notes}</div>}
                </div>
              ))}
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
